import { useRef, useEffect, useContext } from 'react'
import * as React from 'react'
import { classes, style, style as _style } from 'typestyle'
import { Link, useParams } from 'react-router-dom'

/* Hooks ======================================================================================== */
import useValue from '../../hooks/use-value'
import useLocal from '../../hooks/use-local'

/* Common ======================================================================================= */
import config from '../../../config'
import { GLOBAL_CONTENTS } from '../../common/global-contents'
import { flat, structureObj } from '../../share/obj-equal'
import { formatDate } from '../../share/format'

/* Components =================================================================================== */
import { Text } from '../text/text.component'
import { Box } from '../box/box.component'
import { Button } from '../button'
import { Modal } from '../modal'
import { Dropdown } from '../dropdown'
import { Line } from '../line'
import { OperatorWrapper } from '../../core/operators/operator-wrapper'
import { FormActions, FormContext } from '../form'
import { Tooltip } from '../tooltip'

/* Types ======================================================================================== */
import { ComponentSizeType } from '../__core/component.types'

/* Styles ======================================================================================= */
import { tableClass } from './table.class'
import { f18i } from '../../styles/styleset/font-size/f18i'
import { w100 } from '../../styles/styleset/width/w100'
import { p4 } from '../../styles/styleset/padding/p4'
import { paddingLeftInner } from '../../styles/default/padding'
import { f14i } from '../../styles/styleset/font-size/f14i'
import { f12i } from '../../styles/styleset/font-size/f12i'
import { useTheme } from '../theme'
import { p0 } from '../../styles/styleset/padding/p0'

export type Header = {
  handleClick?: () => void
  label: string
  minWidth?: number
  name: string
  primary?: boolean
  renderColumn?: (props: {
    column: any
    columnKey: number
    row: any
    rowKey: number
    name: string
  }) => any
  renderHeader?: (header: any, renderSortIcon: any, key: number) => any
  sort?: number
  width?: number
  type?: string
  style: any
}

type TableProps = {
  style?: React.CSSProperties
  children?: any
  className?: string
  headers: Header[]
  data: any
  actions?: any[]
  selectKey?: string
  onSort?: (header: Header) => void
  onSelect?: (data: any, key?: number) => void
  onMouseDown?: (event: any, selected?: any) => void
  onContextMenu?: (event: any, selected?: any) => void
  onDrop?: (data?: any[], update?: boolean) => void
  onEdit?: (key: any) => void
  topOffset?: number
  size?: ComponentSizeType
  loaded?: boolean
  freeSize?: boolean
  footer?: any
  isSortable?: boolean
  refresh?: any
  $selected?: any
  selected?: any
  isSubmitting?: boolean
  isEditing?: boolean
  isEditable?: boolean
  isViewable?: boolean
  isClickSelectDisabled?: boolean
  values?: any
  base?: any
}

const Swatch = (props: { value: string }) => {
  const { value } = props
  const [theme] = useTheme()
  return (
    <td className={classes(p0)} style={{ position: 'relative', width: '4px' }}>
      <Box
        style={{
          backgroundColor: theme.colors[value]?.swatch,
          position: 'absolute',
          width: '4px',
          height: '100%',
          left: 0,
        }}
      ></Box>
    </td>
  )
}

/* <Table /> ================================================================================= */
const TableComponent: React.FC<TableProps> = props => {
  const {
    children,
    className,
    data,
    footer,
    headers,
    actions = [],
    topOffset = 0,
    refresh,
    isEditing,
    isEditable,
    onEdit,
  } = props

  const test: any = useContext(FormContext)
  const refHeader = useRef() as React.MutableRefObject<HTMLDivElement>
  const refBody = useRef() as React.MutableRefObject<HTMLDivElement>

  const params = useParams()
  const [theme] = useTheme()

  const [local] = useLocal()
  const [internalData, $internalData] = useValue(data)
  const [dragStarted, $dragStarted] = useValue(-1)
  const [dragFocused, $dragFocused] = useValue(-1)

  useEffect(() => {
    let isSyncingHeader = false
    let isSyncingBody = false

    if (refBody.current && refHeader.current) {
      refBody.current.addEventListener('scroll', () => {
        if (!isSyncingBody) {
          isSyncingHeader = true
          refHeader.current.scrollLeft = refBody.current.scrollLeft
        }

        isSyncingBody = false
      })

      refHeader.current.addEventListener('scroll', () => {
        if (!isSyncingHeader) {
          isSyncingBody = true
          refBody.current.scrollLeft = refHeader.current.scrollLeft
        }

        isSyncingHeader = false
      })
    }
  }, [refBody.current, refHeader.current])

  useEffect(() => {
    $internalData.set(data)
  }, [data])

  const renderActionItems = (column: any, key?: any, close?: any) => {
    if (isEditing) {
      return (
        <>
          <Button
            className={classes(w100)}
            baseline="surface"
            label={'Delete'}
            size="md"
            center={false}
            square
            onClick={async () => {
              const _internalData = JSON.parse(JSON.stringify(internalData))
              _internalData.splice(key, 1)

              test.dispatch({
                type: FormActions.SET,
                param: { value: flat(_internalData) },
              })

              await props?.onDrop?.(_internalData)
              close?.()
            }}
          />
        </>
      )
    }

    return [...actions].map((action: any, key: any) => {
      let to = action.to

      if (action.type === 'request' || action.type === 'delete' || action.type === 'field-edit') {
        to = `${action.to.url}`
        to = to.replace('{{rowId}}', column.id)
        to = to.replace('{{groupId}}', column.group)
        to = to.replace('{{baseId}}', column.base)
      }

      const item = {
        ...action,
        onClick: () => {
          Modal.pop()
          switch (action.type) {
            case 'field-edit':
              Modal.push({
                type: 'FieldEdit',
                title: 'Edit Field', // GLOBAL_CONTENTS.editField[local],
                componentProps: {
                  field: {
                    type: 'text',
                    name: 'body.name',
                    label: 'Name',
                  },
                  parentName: '',
                  pathName: to.replace(process.env.REACT_APP_API_URL, ''),
                  // value,
                  onUpdate: refresh,
                },
              })
              break
            case 'request':
            case 'delete': {
              const to = { ...action.to }

              if (to.url) {
                for (const key in params) {
                  to.url = to?.url
                    ?.replace(`{{${key}}}`, params[key])
                    ?.replace('{{rowId}}', column.id)
                }
              }

              Modal.push({
                type: 'Request',
                title: action.description,
                componentProps: {
                  to,
                  onConfirm: () => {
                    refresh()
                  },
                },
              })
              break
            }
            case 'access-manager': {
              for (const key in params) {
                to.url = to.url.replace(`{{${key}}}`, params[key])
              }

              to.url = to.url.replace('{{rowId}}', column.id)

              Modal.push({
                type: 'AccessManager',
                title: 'Manage Access',
                componentProps: {
                  to,
                  label: action.description,
                  onConfirm: () => {
                    refresh()
                  },
                },
              })
              break
            }
          }
          close?.()
        },
        to,
      }

      return (
        <OperatorWrapper key={key} values={column} field={action}>
          {(params: any) => {
            const { hidden } = params

            if (hidden) {
              return <></>
            }

            if (item.to && item.type == 'link') {
              return (
                <Link to={item.to} key={key}>
                  <Button
                    className={classes(w100)}
                    baseline="surface"
                    label={item.label}
                    size="xs"
                    center={false}
                    square
                  />
                </Link>
              )
            }

            if (item.type == 'label') {
              return (
                <Box className={classes(p4, style({ paddingBottom: 2 }))} flex alignCenterY>
                  <Text baseline="surface" size="xs" uppercase shrink semiBold lighter>
                    {item.label}
                  </Text>
                </Box>
              )
            }

            if (item.type == 'break') {
              return (
                <Box
                  className={classes(
                    style({
                      marginTop: 2,
                      marginBottom: 2,
                    }),
                  )}
                  row
                  alignCenterY
                >
                  <Line />
                </Box>
              )
            }

            return (
              <Button
                key={key}
                className={classes(w100)}
                baseline="surface"
                label={item.label}
                size="md"
                center={false}
                onClick={item.onClick}
                square
              />
            )
          }}
        </OperatorWrapper>
      )
    })
  }

  const { base, rowBase } = tableClass.setProps({})

  const baseline = (key: any) => {
    if (props.isSubmitting) {
      return 'background'
    } else if (dragFocused === key || props?.selected?.[key] || props?.selected == key) {
      return 'primary'
    } else {
      return 'surface'
    }
  }

  const getSelectAllText = () => {
    if (Object.entries(props.selected || {}).filter(a => a[1]).length == 0) {
      return 'Select All'
    } else {
      return `Deselect ${Object.entries(props.selected || {}).filter(a => a[1]).length} items`
    }
  }

  const getRowProps = (key: any) => {
    let rowProps: any = {
      className: rowBase,
    }

    if (props.isSortable) {
      rowProps = {
        onDragStart: () => {
          $dragStarted.set(key)
          props.$selected?.reset()
        },
        onDragOver: () => {
          $dragFocused.set(key)
        },
        onDragEnd: async () => {
          if (dragStarted > -1 && !props.isSubmitting) {
            const structured: any = structureObj(props.values)
            const _internalData = Object.keys(structured).map((key: any) => structured[key])
            const row = _internalData[dragStarted]
            _internalData.splice(dragStarted, 1)
            _internalData.splice(dragFocused, 0, row)

            test.dispatch({
              type: FormActions.SET,
              param: { value: flat(_internalData) },
            })

            $dragFocused.set(-1)
            $dragStarted.set(-1)

            // await props?.onDrop?.(_internalData)
          }
        },
        draggable: !props.isSubmitting,
        style: {
          cursor: 'pointer',
        },
        className: rowBase,
      }
    }

    return rowProps
  }

  return (
    <>
      <Box
        flex
        baseline="surface"
        alignCenterY
        className={rowBase}
        style={{
          top: topOffset,
          position: 'sticky',
          overflowY: 'hidden',
          zIndex: 2,
        }}
      >
        {/* HEADER */}
        <Box
          ref={refHeader}
          flex
          alignCenterX
          className={classes('aeki-table', base, className)}
          style={{
            height: 40,
          }}
        >
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              height: 40,
            }}
          >
            <Box as={<thead />} style={{ height: 0 }}>
              <tr style={{ height: 0 }}>
                {headers.map((header: any, key: any) => {
                  return (
                    <td
                      className={paddingLeftInner}
                      style={{
                        textAlign: 'left',
                        minWidth: header.style.minWidth,
                        height: 40,
                        ...(header.type === 'swatch' ? { minWidth: 0, padding: 0 } : {}),
                      }}
                      key={key}
                    >
                      <Box alignCenterY fullHeight>
                        <Text size="sm" semiBold baseline="surface" shrink>
                          {header.label}
                        </Text>
                      </Box>
                    </td>
                  )
                })}
              </tr>
            </Box>
            <tbody
              style={{
                opacity: 0,
                height: 0,
              }}
              className={style({
                // visibility: 'hidden',
                visibility: 'collapse',
                $nest: {
                  '&:*': {
                    lineHeight: '0px !important',
                    visibility: 'hidden',
                  },
                },
              })}
            >
              {internalData.map((row: any, key: any) => {
                const flattenRow = flat(row)

                return (
                  <tr key={key} style={{ lineHeight: 0 }}>
                    {headers.map((header: any, key2: any) => {
                      let value =
                        header.translate && config.dictionary?.[flattenRow[header.name]]?.[local]
                          ? config.dictionary?.[flattenRow[header.name]]?.[local]
                          : flattenRow[header.name]

                      const style: React.CSSProperties = { textAlign: 'left', height: 54 }

                      switch (header.type) {
                        case 'date':
                          value = formatDate(value, { time: true })
                          break
                        case 'order':
                          value += 1
                          break
                        case 'swatch':
                          return <Swatch value={value} key={key2} />
                      }

                      return (
                        <td className={paddingLeftInner} style={style} key={key2}>
                          {header.renderColumn ? (
                            header.renderColumn({
                              column: flattenRow[header.name],
                              columnKey: key2,
                              row: flattenRow,
                              rowKey: key,
                              ...header,
                            })
                          ) : (
                            <Box alignCenterY fullHeight>
                              <Text size="sm" baseline="surface" shrink>
                                {flattenRow[header.name] !== undefined ? value : '-'}
                              </Text>
                            </Box>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>
        <table style={{ borderCollapse: 'collapse' }}>
          <Box as={<thead />} style={{}}>
            <tr>
              <td
                className={paddingLeftInner}
                style={{
                  textAlign: 'left',
                  height: 0,
                }}
              >
                <Box alignCenterY fullHeight relative>
                  {/* <Dropdown
                    floatProps={{
                      anchorPosition: 'bottom right',
                      dialogPosition: 'top right',
                      width: 'fit-content',
                      fixed: true,
                    }}
                  >
                    <Button
                      size="sm"
                      baseline="disabled"
                    icon={<span className={classes('material-icons', f18i)}>more_horiz</span>}
                    />
                    {(close: any) => {
                      return (
                        <Box
                          baseline="surface"
                          className={classes(p4, _style({ width: 'fit-content' }))}
                          rounded
                          shadow
                          flex
                          row
                        >
                          <Button
                            className={classes(w100)}
                            baseline="disabled"
                            label={getSelectAllText()}
                            size="md"
                            center={false}
                            onClick={() => {
                              if (
                                Object.entries(props.selected || {}).filter(a => a[1]).length === 0
                              ) {
                                close()
                                const selected: any = {}
                                internalData.map((a: any, key: any) => {
                                  selected[key] = true
                                })

                                props.$selected?.set?.(selected)
                              } else {
                                close()
                                props.$selected?.reset?.()
                              }
                            }}
                            square
                          />
                        </Box>
                      )
                    }}
                  </Dropdown> */}
                  {(props.isEditable || props.isViewable) && (
                    <Box style={{ height: 30, width: 30 }} />
                  )}
                  {actions?.filter(a => !a.hidden)?.length > 0 && (
                    <Box style={{ height: 30, width: 30 }} />
                  )}

                  {Object.entries(props.selected || {}).filter(a => a[1]).length > 0 && (
                    <Box
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 10,
                        right: -4,
                        top: -4,
                      }}
                      baseline="background"
                      alignCenterX
                      alignCenterY
                      absolute
                    >
                      <Text size="xs" shrink>
                        {Object.entries(props.selected || {}).filter(a => a[1]).length}
                      </Text>
                    </Box>
                  )}
                </Box>
              </td>
            </tr>
          </Box>
        </table>
        {/* <Box absolute fullHeight fullWidth baseline="none" /> */}
      </Box>
      {/* BODY */}
      <Box flex baseline="none">
        <div
          ref={refBody}
          className={classes('aeki-table', base, className)}
          style={{ overflowY: 'hidden' }}
        >
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <Box
              as={<thead />}
              style={{
                visibility: 'collapse',
                transform: 'scaleY(0)',
                height: 0,
                lineHeight: '0px',
              }}
            >
              <tr>
                {headers.map((header: any, key: any) => {
                  return (
                    <td
                      className={paddingLeftInner}
                      style={{
                        textAlign: 'left',
                        minWidth: header.style.minWidth,
                        height: 0,
                        ...(header.type === 'swatch' ? { minWidth: 0, padding: 0 } : {}),
                      }}
                      key={key}
                    >
                      <Box alignCenterY fullHeight>
                        <Text
                          style={{
                            height: 0,
                          }}
                          size="sm"
                          semiBold
                          baseline="surface"
                          shrink
                        >
                          {header.label}
                        </Text>
                      </Box>
                    </td>
                  )
                })}
              </tr>
            </Box>
            <tbody>
              {internalData.map((row: any, key: any) => {
                const flattenRow = flat(row)
                const tableColorCodes = props.base?.properties?.tableColorCode
                const highlightColor = tableColorCodes?.options?.[flattenRow[tableColorCodes?.key]]

                return (
                  <Box
                    key={key}
                    // baseline="primary"
                    baseline={baseline(key)}
                    as={<tr />}
                    onClick={() => {
                      const prev = { ...(props.selected || {}) }
                      prev[key] = !prev[key]

                      if (!props.isClickSelectDisabled) {
                        props.$selected?.set(prev)
                      }
                    }}
                    {...getRowProps(key)}
                  >
                    {headers.map((header: any, key2: any) => {
                      let value =
                        header.translate && config.dictionary?.[flattenRow[header.name]]?.[local]
                          ? config.dictionary?.[flattenRow[header.name]]?.[local]
                          : flattenRow[header.name]

                      switch (header.type) {
                        case 'date':
                          value = formatDate(value, { time: true })
                          break
                        case 'order':
                          value += 1
                          break
                        case 'swatch':
                          return <Swatch value={value} key={key2} />
                      }

                      return (
                        <td
                          className={paddingLeftInner}
                          style={{
                            textAlign: 'left',
                            height: 54,
                            position: 'relative',
                          }}
                          key={key2}
                        >
                          {key2 === 0 && (
                            <Box
                              absolute
                              style={{
                                left: 0,
                                top: 0,
                                height: '100%',
                                width: 2,
                                background: highlightColor,
                              }}
                            />
                          )}
                          {header.renderColumn ? (
                            header.renderColumn({
                              column: flattenRow[header.name],
                              columnKey: key2,
                              row: flattenRow,
                              rowKey: key,
                              ...header,
                            })
                          ) : (
                            <Box alignCenterY fullHeight baseline="none">
                              <Text size="sm" baseline="surface" shrink>
                                {flattenRow[header.name] !== undefined ? value : '-'}
                              </Text>
                            </Box>
                          )}
                        </td>
                      )
                    })}
                  </Box>
                )
              })}
            </tbody>
          </table>
        </div>
        <table style={{ borderCollapse: 'collapse', overflow: 'hidden' }}>
          <Box
            as={<thead />}
            style={{
              visibility: 'collapse',
              transform: 'scaleY(0)',
              height: 0,
              lineHeight: '0px',
            }}
          >
            <tr>
              <td
                className={paddingLeftInner}
                style={{
                  textAlign: 'left',
                  height: 0,
                }}
              >
                <Box alignCenterY fullHeight>
                  <Text
                    style={{
                      height: 0,
                    }}
                    size="sm"
                    semiBold
                    baseline="surface"
                  >
                    {'E'}
                  </Text>
                </Box>
              </td>
            </tr>
          </Box>
          <tbody>
            {/* {[...internalData, ...(footer ? footer : [])].map((row: any, key: any) => { */}
            {[...internalData, ...(footer ? footer : [])].map((row: any, key: any) => {
              const flattenRow = flat(row)

              return (
                <React.Fragment key={key}>
                  <Box baseline={baseline(key)} as={<tr />} className={rowBase} relative>
                    <td className={paddingLeftInner} style={{ textAlign: 'left', height: 54 }}>
                      <Box baseline="none" alignCenterX alignCenterY fullWidth fullHeight>
                        {props.isEditable && (
                          <Button
                            size="xs"
                            baseline={props?.selected == key ? 'none' : 'surface'}
                            icon={<span className={classes('material-icons', f12i)}>edit</span>}
                            onClick={() => {
                              props.$selected.set(key)
                              props?.onEdit?.(key)
                            }}
                            square
                          />
                        )}
                        {!props.isEditable && props.isViewable && (
                          <Button
                            size="xs"
                            baseline={props?.selected == key ? 'none' : 'surface'}
                            icon={
                              <span className={classes('material-icons', f14i)}>visibility</span>
                            }
                            onClick={() => {
                              props.$selected.set(key)
                            }}
                            square
                          />
                        )}
                        {actions?.filter(a => !a.hidden)?.length > 0 && (
                          <Dropdown
                            floatProps={{
                              anchorPosition: 'bottom right',
                              dialogPosition: 'top right',
                              width: 'fit-content',
                              flip: true,
                              fullWidth: true,
                            }}
                          >
                            <Tooltip label={GLOBAL_CONTENTS.moreActions[local]}>
                              <Button
                                size="xs"
                                baseline={props?.selected == key ? 'none' : 'surface'}
                                icon={
                                  <span className={classes('material-icons', f18i)}>
                                    more_horiz
                                  </span>
                                }
                                square
                              />
                            </Tooltip>
                            {(close: any) => {
                              return (
                                <Box
                                  baseline="surface"
                                  className={classes(p4, _style({ width: 'fit-content' }))}
                                  rounded
                                  shadow
                                  flex
                                  row
                                >
                                  {renderActionItems(flattenRow, key, close)}
                                </Box>
                              )
                            }}
                          </Dropdown>
                        )}
                      </Box>
                    </td>
                  </Box>
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </Box>
      {children}
    </>
  )
}

/* Export ======================================================================================= */
export const Table = TableComponent
